import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { fetchAdminDashboard } from '../api/adminApi';
import { fetchAdminAnalytics, type AnalyticsPeriod } from '../api/adminAnalyticsApi';
import { fetchAdminErrors } from '../api/adminErrorsApi';
import { fetchAdminMetrika } from '../api/adminMetrikaApi';
import { fetchAdminProduct } from '../api/adminProductApi';
import { fetchAdminElevenlabs, fetchAdminStories } from '../api/adminStoriesApi';

export const adminKeys = {
  dashboard: ['admin', 'dashboard'] as const,
  analytics: (days: AnalyticsPeriod) => ['admin', 'analytics', days] as const,
  errors: (days: AnalyticsPeriod) => ['admin', 'errors', days] as const,
  product: (days: AnalyticsPeriod) => ['admin', 'product', days] as const,
  metrika: (days: AnalyticsPeriod) => ['admin', 'metrika', days] as const,
  stories: (days: AnalyticsPeriod) => ['admin', 'stories', days] as const,
  elevenlabs: ['admin', 'elevenlabs'] as const,
};

export function useAdminDashboardQuery() {
  return useQuery({
    queryKey: adminKeys.dashboard,
    queryFn: fetchAdminDashboard,
    // Сводка по всем юзерам — лёгкая агрегация, но и не realtime. 30с кеша.
    staleTime: 30_000,
    // Не ретраим: основной не-2xx здесь — 403 для гостя, повтор не поможет.
    retry: false,
  });
}

export function useAdminAnalyticsQuery(days: AnalyticsPeriod) {
  return useQuery({
    queryKey: adminKeys.analytics(days),
    queryFn: () => fetchAdminAnalytics(days),
    staleTime: 60_000,
    // Смена периода держит прошлые графики (приглушёнными), без скелетона и прыжка.
    placeholderData: keepPreviousData,
    retry: false,
  });
}

export function useAdminErrorsQuery(days: AnalyticsPeriod) {
  return useQuery({
    queryKey: adminKeys.errors(days),
    queryFn: () => fetchAdminErrors(days),
    // Журнал ошибок — только Redis, запрос лёгкий: обновляем раз в минуту, пока страница открыта.
    staleTime: 30_000,
    refetchInterval: 60_000,
    placeholderData: keepPreviousData,
    retry: false,
  });
}

/** Аналитика учеников (2026-10-21): когорты, возвраты, воронка, функции, письма. */
export function useAdminProductQuery(days: AnalyticsPeriod, enabled = true) {
  return useQuery({
    queryKey: adminKeys.product(days),
    queryFn: () => fetchAdminProduct(days),
    staleTime: 60_000,
    placeholderData: keepPreviousData,
    retry: false,
    enabled,
  });
}

/** Яндекс Метрика: визиты и регистрации по источникам (сервер кэширует на 15 минут). */
export function useAdminMetrikaQuery(days: AnalyticsPeriod, enabled = true) {
  return useQuery({
    queryKey: adminKeys.metrika(days),
    queryFn: () => fetchAdminMetrika(days),
    staleTime: 300_000,
    placeholderData: keepPreviousData,
    retry: false,
    enabled,
  });
}

/** «Истории»: воронка игры, перепрохождения, задание дня и босс (2026-10-22). */
export function useAdminStoriesQuery(days: AnalyticsPeriod, enabled = true) {
  return useQuery({
    queryKey: adminKeys.stories(days),
    queryFn: () => fetchAdminStories(days),
    staleTime: 60_000,
    placeholderData: keepPreviousData,
    retry: false,
    enabled,
  });
}

/** Остаток кредитов ElevenLabs (сервер кэширует на 10 минут). */
export function useAdminElevenlabsQuery(enabled = true) {
  return useQuery({ queryKey: adminKeys.elevenlabs, queryFn: fetchAdminElevenlabs, staleTime: 300_000, retry: false, enabled });
}
